import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useCourse, useCompletion, useCompleteCourse } from "@/hooks/useCourses";
import { usePublicSettings } from "@/hooks/useSettings";
import { courseService } from "@/services/courseService";
import { GettingStartedCard, GETTING_STARTED_ID } from "@/components/GettingStartedCard";
import { AnimatedStage } from "@/components/AnimatedStage";
import { CourseBrand } from "@/components/CourseBrand";
import { PetalCelebration } from "@/plugins/vibeDojo/PetalCelebration";
import { DojoControls } from "@/plugins/vibeDojo/DojoControls";
import { fmtClock, fmtDuration, estimateSeconds, courseSeconds } from "@/lib/duration";
import { apiError } from "@/lib/api";
import { dojo } from "@/plugins/vibeDojo/dojoAudio";
import {
  Play, Pause, SkipBack, SkipForward, Volume2, ArrowLeft, GraduationCap,
  CheckCircle2, Loader2, List, Award, MessageCircleQuestion, Send, ArrowRight,
} from "lucide-react";

export default function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Duck the Dojo ambient bed to 50% while a course (with narration) is open.
  useEffect(() => { dojo.setDuck(true); return () => dojo.setDuck(false); }, []);
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioCache] = useState(() => ({}));
  const [completed, setCompleted] = useState({});
  const [phase, setPhase] = useState("intro");
  const [dur, setDur] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaAnswer, setQaAnswer] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const qaAudioRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const lastSaveRef = useRef(0);
  const resumedRef = useRef(false);

  const { data: course, isError } = useCourse(id);
  const { data: completion } = useCompletion(id);
  const { data: publicSettings } = usePublicSettings();
  const qaEnabled = publicSettings?.qa_enabled !== false;
  const isComplete = completion?.completed;
  const [downloading, setDownloading] = useState(false);

  const completeMutation = useCompleteCourse({
    onSuccess: (d) => {
      if (d?.completed) {
        toast.success(course?.length === "comprehensive"
          ? "Course completed! Your certificate is ready."
          : "Course completed!");
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // Record completion on the backend (idempotent). The petal celebration is
  // triggered separately at the finish moment so it fires even when the course
  // was already completed (e.g. a re-watch or a resumed finish).
  const markComplete = () => { if (!isComplete && !completeMutation.isPending) completeMutation.mutate(id); };
  const finishCourse = () => { markComplete(); setCelebrate(true); };

  const downloadCert = async () => {
    setDownloading(true);
    try {
      await courseService.downloadCertificate(id, course?.title);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (isError) {
      toast.error("Course not found");
      navigate("/dashboard");
    }
  }, [isError, navigate]);

  // Universal Resume: on first load, jump back to the saved chapter + timestamp.
  useEffect(() => {
    if (!course || completion === undefined || resumedRef.current) return;
    resumedRef.current = true;
    if (completion?.completed) return;
    courseService.getProgress(id).then((p) => {
      if (p?.found && (p.section_idx > 0 || p.position_seconds > 3)) {
        const si = Math.min(p.section_idx, course.sections.length - 1);
        setIdx(si);
        pendingSeekRef.current = { idx: si, pos: p.position_seconds };
        toast.info("Resumed where you left off");
      }
    }).catch(() => {});
  }, [course, completion, id]);

  const loadAudio = useCallback(async (i) => {
    if (audioCache[i]) return audioCache[i];
    setLoadingAudio(true);
    try {
      const audio = await courseService.sectionAudio(id, i);
      // Decode base64 to a Blob URL — data: URIs report duration=Infinity in Chrome,
      // which breaks the "ended" auto-advance and the duration readout.
      const bin = atob(audio);
      const bytes = new Uint8Array(bin.length);
      for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
      const src = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
      audioCache[i] = src;
      return src;
    } catch (e) {
      toast.error("Couldn't load narration");
      return null;
    } finally {
      setLoadingAudio(false);
    }
  }, [id, audioCache]);

  useEffect(() => () => {
    Object.values(audioCache).forEach((u) => {
      if (typeof u === "string" && u.startsWith("blob:")) URL.revokeObjectURL(u);
    });
  }, [audioCache]);

  const playSection = useCallback(async (i, autoplay = true) => {
    setIdx(i);
    setProgress(0);
    setCurTime(0);
    setDur(0);
    const src = await loadAudio(i);
    if (!src || !audioRef.current) return;
    audioRef.current.src = src;
    if (autoplay) {
      try { await audioRef.current.play(); setPlaying(true); } catch { setPlaying(false); }
    }
  }, [loadAudio]);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (!audioRef.current.src) { await playSection(idx, true); return; }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch {} }
  };

  const onEnded = () => {
    setCompleted((c) => ({ ...c, [idx]: true }));
    if (course && idx < course.sections.length - 1) {
      playSection(idx + 1, true);
    } else {
      setPlaying(false);
      if (qaEnabled) {
        setPhase("qa");
      } else {
        finishCourse();
        setPhase("outro");
      }
    }
  };

  const askQuestion = async () => {
    const q = qaQuestion.trim();
    if (!q) return;
    setQaLoading(true);
    setQaAnswer(null);
    try {
      const res = await courseService.askCourseQuestion(id, q);
      setQaAnswer(res.answer);
      if (res.audio && qaAudioRef.current) {
        qaAudioRef.current.src = `data:audio/mpeg;base64,${res.audio}`;
        qaAudioRef.current.play().catch(() => {});
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setQaLoading(false);
    }
  };

  const finishFromQa = () => {
    if (qaAudioRef.current) { try { qaAudioRef.current.pause(); } catch {} }
    finishCourse();
    setPhase("outro");
  };

  const onTime = () => {
    const a = audioRef.current;
    if (a && a.duration && isFinite(a.duration)) {
      setProgress((a.currentTime / a.duration) * 100);
      setCurTime(a.currentTime);
      // Persist resume position every ~10s of playback (not once complete).
      const now = Date.now();
      if (!isComplete && now - lastSaveRef.current > 10000) {
        lastSaveRef.current = now;
        courseService.saveProgress(id, idx, a.currentTime).catch(() => {});
      }
    }
  };

  const onMeta = () => {
    const a = audioRef.current;
    if (a && isFinite(a.duration)) {
      setDur(a.duration);
      const seek = pendingSeekRef.current;
      if (seek && seek.idx === idx && seek.pos > 0) {
        try { a.currentTime = Math.min(seek.pos, a.duration - 0.5); } catch {}
      }
      pendingSeekRef.current = null;
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const section = course.sections[idx];
  const isComprehensive = course.length === "comprehensive";
  const totalSeconds = course.duration_seconds ?? courseSeconds(course.sections);

  return (
    <div className="min-h-screen bg-[#050505] text-[#FAFAFA] dojo-page">
      <audio ref={audioRef} onEnded={onEnded} onTimeUpdate={onTime} onLoadedMetadata={onMeta} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />

      <PetalCelebration show={celebrate} onDone={() => setCelebrate(false)} />
      <audio ref={qaAudioRef} />

      {phase === "qa" && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="qa-slide">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-2 text-primary mb-3">
              <MessageCircleQuestion className="h-6 w-6" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold">Questions?</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <div className="flex-1 flex items-center gap-2 border border-white/25 bg-white/5 px-4 py-3 focus-within:border-primary transition-colors">
                <MessageCircleQuestion className="h-4 w-4 text-white/50 shrink-0" />
                <input
                  value={qaQuestion}
                  onChange={(e) => setQaQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !qaLoading && askQuestion()}
                  placeholder="e.g. How does this apply in real life?"
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/40"
                  data-testid="qa-question-input"
                />
              </div>
              <button onClick={askQuestion} disabled={qaLoading || !qaQuestion.trim()}
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                data-testid="qa-ask-button">
                {qaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Ask
              </button>
            </div>

            {qaAnswer && (
              <div className="mt-6 border border-primary/30 bg-primary/5 p-5" data-testid="qa-answer">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-2">Answer</p>
                <p className="text-white/90 leading-relaxed">{qaAnswer}</p>
                <p className="mt-3 text-xs text-white/50">Saved to your Personal FAQ.</p>
              </div>
            )}

            <div className="mt-8 flex justify-end">
              <button onClick={finishFromQa}
                className="inline-flex items-center gap-2 text-white/80 hover:text-white border border-white/25 hover:border-white/60 px-5 py-2.5 text-sm font-semibold transition-colors"
                data-testid="qa-continue-button">
                {qaAnswer ? "Continue" : "Skip"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {phase === "intro" && <CourseBrand mode="intro" onDone={() => setPhase("course")} />}
        {phase === "outro" && <CourseBrand mode="outro" onDone={() => setPhase("course")} />}
      </AnimatePresence>

      {phase === "outro" && (
        <button
          onClick={() => navigate("/faq")}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-5 py-2.5 text-sm font-semibold backdrop-blur-md transition-colors"
          data-testid="outro-review-qa-button"
        >
          <MessageCircleQuestion className="h-4 w-4" /> Review your course Q&amp;As
        </button>
      )}

      {/* Top bar */}
      <div className="border-b border-white/10 px-6 h-16 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4" /> Library
        </button>
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold">
          <GraduationCap className="h-5 w-5 text-primary" /> Learn Me
        </Link>
        <span className="text-xs uppercase tracking-wider text-white/50">{course.length_label} · {fmtDuration(totalSeconds)}</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px]">
        {/* Stage */}
        <div className="p-6 md:p-10">
          <AnimatedStage
            section={section}
            courseTitle={course.title}
            idx={idx}
            total={course.sections.length}
            progress={progress}
            playing={playing}
            loadingAudio={loadingAudio}
          />

          {/* Controls */}
          <div className="mt-4">
            <div className="h-1.5 bg-white/10 w-full cursor-pointer" onClick={(e) => {
              const a = audioRef.current; if (!a || !a.duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration;
            }} data-testid="progress-bar">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-3">
                <button onClick={() => idx > 0 && playSection(idx - 1, true)} disabled={idx === 0}
                  className="h-10 w-10 flex items-center justify-center border border-white/15 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="prev-button">
                  <SkipBack className="h-4 w-4" />
                </button>
                <button onClick={togglePlay} className="h-12 w-12 flex items-center justify-center bg-primary hover:opacity-90 transition-opacity" data-testid="play-pause-button">
                  {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                </button>
                <button onClick={() => idx < course.sections.length - 1 && playSection(idx + 1, true)} disabled={idx === course.sections.length - 1}
                  className="h-10 w-10 flex items-center justify-center border border-white/15 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="next-button">
                  <SkipForward className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 text-white/50 ml-2">
                  <Volume2 className="h-4 w-4" />
                </div>
                <span className="text-xs text-white/60 font-mono ml-1 tabular-nums" data-testid="time-readout">
                  {fmtClock(curTime)} / {dur ? fmtClock(dur) : fmtClock(section.seconds ?? estimateSeconds(section.narration))}
                </span>
              </div>
              <div className="text-sm text-white/60">{course.title}</div>
            </div>

            {/* Completion & certificate */}
            <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="completion-bar">
              {isComplete ? (
                <>
                  <span className="inline-flex items-center gap-2 text-primary text-sm font-semibold" data-testid="course-completed-badge">
                    <CheckCircle2 className="h-4 w-4" /> Completed
                  </span>
                  {isComprehensive ? (
                    <button onClick={downloadCert} disabled={downloading}
                      className="inline-flex items-center gap-2 border border-primary text-primary px-4 h-10 hover:bg-primary hover:text-white transition-colors text-sm font-semibold" data-testid="download-certificate-button">
                      {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />} Download certificate
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-sm text-white/60" data-testid="certificate-hint">
                      <Award className="h-4 w-4 text-primary" /> Master the full training to earn your certificate.
                    </span>
                  )}
                </>
              ) : (
                <button onClick={finishCourse} disabled={completeMutation.isPending}
                  className="inline-flex items-center gap-2 bg-primary text-white px-4 h-10 hover:opacity-90 transition-opacity text-sm font-semibold" data-testid="mark-complete-button">
                  {completeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark as complete
                </button>
              )}
            </div>
          </div>

          {/* Transcript */}
          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-3">Transcript</p>
            <p className="text-white/70 leading-relaxed" data-testid="transcript">{section.narration}</p>
          </div>

          {/* Expert course extras: intro, key points, sources */}
          {course.intro_paragraph && (
            <div className="mt-8 border-t border-white/10 pt-6" data-testid="expert-intro">
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Introduction</p>
              <p className="text-white/75 leading-relaxed italic">{course.intro_paragraph}</p>
            </div>
          )}
          {course.key_points?.length > 0 && (
            <div className="mt-8 border-t border-white/10 pt-6" data-testid="expert-key-points">
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Key Points</p>
              <ul className="space-y-2">
                {course.key_points.map((k, i) => (
                  <li key={i} className="flex gap-3 text-white/75 leading-relaxed">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-1" /> <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {course.sources?.length > 0 && (
            <div className="mt-8 border-t border-white/10 pt-6" data-testid="expert-sources">
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">List of Sources</p>
              <ul className="space-y-1.5 list-decimal list-inside text-white/70">
                {course.sources.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Chapters sidebar */}
        <aside className="border-l border-white/10 p-6 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto" data-testid="chapter-list">
          <div className="mb-6"><DojoControls /></div>
          <div className="flex items-center gap-2 mb-1">
            <List className="h-4 w-4 text-primary" />
            <h3 className="font-display font-bold">Chapters</h3>
          </div>
          <p className="text-xs text-white/40 mb-5">{course.level} · {course.sections.length} chapters · {fmtDuration(totalSeconds)}</p>
          <div className="space-y-1">
            {course.sections.map((s, i) => (
              <button key={i} onClick={() => playSection(i, true)}
                className={`w-full text-left p-3 flex items-start gap-3 transition-colors ${i === idx ? "bg-primary/15 border-l-2 border-primary" : "hover:bg-white/5 border-l-2 border-transparent"}`}
                data-testid={`chapter-${i}`}>
                <span className="text-xs font-mono text-white/40 mt-0.5 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className={`text-sm leading-snug flex-1 ${i === idx ? "text-white font-semibold" : "text-white/70"}`}>{s.title}</span>
                <span className="text-[10px] font-mono text-white/35 shrink-0 mt-1" data-testid={`chapter-${i}-duration`}>{fmtDuration(s.seconds ?? estimateSeconds(s.narration))}</span>
                {completed[i] && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
              </button>
            ))}
          </div>
        </aside>
      </div>

      {id !== GETTING_STARTED_ID && (
        <div className="border-t border-white/10 p-6 md:p-8" data-testid="getting-started-pin">
          <GettingStartedCard />
        </div>
      )}
    </div>
  );
}
